import dayjs from "dayjs";
import { Constant } from "app/constants/constant";
import { ReportAdminService } from "app/services/report/ReportAdminService";
import { ResponseResultCode } from "app/model/response-api";
import { useEffect, useState } from "react";
import CountUp from "react-countup";
import Highcharts from "highcharts/highstock";
import { HighchartsOptionsUtil } from "app/utils/highchartsOptionsUtil";
import { numberFormatter } from "app/utils/stringUtil";
import HighchartsReact from "highcharts-react-official";
import { RdmpCondService } from "app/services/rp/RdmpCondService";
import { useNavigate } from "react-router-dom";
import ArcButton, { ButtonIcon, ButtonType } from "app/components/arc/ArcButton";
import DashboardLoading from "app/pages/db/modules/DashboardLoading";
import useInterval from "app/hooks/use-interval";

const defaultOption: Highcharts.Options = {
  chart: {
    type: "pie",
    backgroundColor: "transparent",
    plotBackgroundColor: undefined,
    plotShadow: false,
    height: 270,
    margin: [-15, 0, 50, 0],
    spacing: [0, 0, 0, 0],

  },
  legend: {
    layout: "horizontal",
    verticalAlign: "bottom",
    align: "center",
    alignColumns: true,
    width: 365,
    itemWidth: 120,
    itemDistance: 0,
    symbolHeight: 0,
    symbolWidth: 0,
    squareSymbol: false,
    useHTML: true,
    labelFormatter: function () {
      return (`
        <span style="display: flex; width:145px; justify-content: space-between; align-items: center; padding: 4px 0;">
          <span style="width: 73%; text-align:left;display:flex;align-items:center;">
            <em style="display:inline-block; min-width:18px; height:18px; border-radius: 50%; border: 5px solid ${this.color};clip-path: polygon(0 0, 50% 0, 50% 100%, 0 100%);"></em>
            <span style="font-size:.7rem;white-space:initial;">
              ${this.name}
            </span>
          </span>
        </span>`
      );
    },
  },
  tooltip: {
    useHTML: true,
    style: {
      fontSize: '12px',
    },
    formatter: function (this: any): any {
      return `<div>
                <span style="color:${this.color}">●&nbsp;</span>
                <span style='font-weight:700;'>${this.point.name}:&nbsp;</span>
                <span style='font-weight:500;'>${numberFormatter(this.point.y, 2)}</span> MWh
              </div>`;
    },
  },
  plotOptions: {
    pie: {
      allowPointSelect: true,
      cursor: "pointer",
      showInLegend: true,
      innerSize: "75%",
      size: "80%",
      borderWidth: 0,
      borderRadius: 0,
      dataLabels: [{
        useHTML: true,
        enabled: true,
        distance: -5,
        x: -5,
        y: -5,
        formatter(this:any) {
          const point: any = this;
          return (`
            <div style="text-align:center;">
              <span class="bar-graph-color-num" style="position:relative;display:inline-block;margin-top:5px;padding:5px 20px;font-weight:400;border-radius:15px;overflow:hidden;background:${point.color};">
                <span style="position: relative; display: block; z-index: 1; color: #fff; font-size: 13px; font-weight: 600;">
                  ${numberFormatter(point.percentage, 2)}%
                </span>
                <em style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, .3);"></em>
              </span>
            </div>
          `);
        },
      }],
    },
  },
  series: [],
};

interface FuflData {
  selfGen: number;
  rec: number;
  green: number;
  drcPpa: number;
  thdptPpa: number;
}

export default function DbFuflStatus() {
  Highcharts.setOptions({
    ...HighchartsOptionsUtil.locale.kr.globalOptions,
    title: { text: "" },
    colors: ["#545CC1", "#4C96F9", "#44C5DB", "#9EFFDE", "#85A0ED"],
    exporting: {enabled: false},
    credits: {enabled: false},
    tooltip: {shared: true, headerFormat: ''},
    accessibility: {enabled: false},
  });

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const initFufl: FuflData = { selfGen: 0, rec: 0, green: 0, drcPpa: 0, thdptPpa: 0 };
  const [fufl, setFufl] = useState<FuflData>(initFufl);

  const [option, setOption] = useState<Highcharts.Options>(defaultOption);

  useEffect(() => {
    fetch();
  }, []);

  useInterval(() => {
    fetch();
  }, 600000);

  function fetch() {
    Promise.all([fetchFufl(), fetchRdmpCont()])
    .then(response => {
      const data = {
        selfGen: response[0].selfGen/1000,
        rec: response[0].rec/1000,
        green: response[0].green/1000,
        drcPpa: response[0].drcPpa/1000,
        thdptPpa: response[0].thdptPpa/1000,
      }

      setFufl({
        selfGen: data.selfGen,
        rec: data.rec,
        green: data.green,
        drcPpa: data.drcPpa,
        thdptPpa: data.thdptPpa,
      });

      setOption({
        ...defaultOption,
        subtitle: {
          useHTML: true,
          align: 'center',
          verticalAlign: 'middle',
          y: -21,
          text: `
            <div style="text-align: center;font-size:.7rem;font-family:Pretendard sans-serif;color:#474747;">
              이행기업 수<br/>
              <span style="font-size:1.5rem;font-weight:800;font-family:Pretendard sans-serif;color:#444444;">
                ${numberFormatter(response[1])}
              </span>개
            </div>
          `
        },
        series: [{
          type: 'pie',
          data: [
            { name: '자가발전', y: data.selfGen },
            { name: 'REC', y: data.rec },
            { name: '녹색프리미엄', y: data.green  },
            { name: '직접PPA', y: data.drcPpa },
            { name: '제3자PPA', y: data.thdptPpa },
          ],
          borderWidth: (
              response[0].selfGen === 0 && response[0].rec === 0 &&
              response[0].green === 0 && response[0].drcPpa === 0 &&
              response[0].thdptPpa === 0
          ) ? 0.5 : 0,
        }]
      });
    })
    .finally(() => {
      setIsLoading(false);
    });
  }

  async function fetchFufl() {
    const searchType: string = 'MONTH';
    const strtYear: string = dayjs().startOf('year').format(Constant.FORMAT_YMDHMS);
    const endYear: string = dayjs().endOf('year').format(Constant.FORMAT_YMDHMS);

    const param = { searchType: searchType, fromDt: strtYear, toDt: endYear, complxCd: '' };
    const response = await ReportAdminService.getInstance().getReportDetail004(param);
    if (response && ResponseResultCode.Y === response.resultCode) {
      return {
        selfGen: response.data?.selfgen ?? 0,
        rec: response.data?.rec ?? 0,
        green: response.data?.grnprm ?? 0,
        drcPpa: response.data?.drcPpa ?? 0,
        thdptPpa: response.data?.thdptPpa ?? 0,
      };
    }
    return { selfGen: 0, rec: 0, green: 0, drcPpa: 0, thdptPpa: 0 };
  }

  async function fetchRdmpCont() {
    const response = await RdmpCondService.getInstance().getRdmpCount();
    if (response && ResponseResultCode.Y === response.resultCode) {
      return response.data ?? 0;
    }
    return 0;
  }

  return (
    <div className="cont">
      <DashboardLoading isLoading={isLoading} />
      <h2 className="titT2 bl"><span>산단 이행수단 현황</span></h2>
      <div className="chart">
        {option && (
          <HighchartsReact highcharts={Highcharts} options={option} />
        )}
      </div>
      <div className="txt-flex2 mgt10">
        <div>
          <p className="tit">자가발전</p>
          <strong>
            <CountUp end={fufl.selfGen} decimals={1} duration={1}>
              {({ countUpRef }) => <em ref={countUpRef} />}
            </CountUp>MWh
          </strong>
        </div>
        <div>
          <p className="tit">REC</p>
          <strong>
            <CountUp end={fufl.rec} decimals={1} duration={1}>
              {({ countUpRef }) => <em ref={countUpRef} />}
            </CountUp>MWh
          </strong>
        </div>
        <div>
          <p className="tit">녹색프리미엄</p>
          <strong>
            <CountUp end={fufl.green} decimals={1} duration={1}>
              {({ countUpRef }) => <em ref={countUpRef} />}
            </CountUp>MWh
          </strong>
        </div>
        <div>
          <p className="tit">직접PPA</p>
          <strong>
            <CountUp end={fufl.drcPpa} decimals={1} duration={1}>
              {({ countUpRef }) => <em ref={countUpRef} />}
            </CountUp>MWh
          </strong>
        </div>
        <div>
          <p className="tit">제3자PPA</p>
          <strong>
            <CountUp end={fufl.thdptPpa} decimals={1} duration={1}>
              {({ countUpRef }) => <em ref={countUpRef} />}
            </CountUp>MWh
          </strong>
        </div>
      </div>
      <ArcButton
        type={ButtonType.custom}
        className="more-btn"
        icon={ButtonIcon.plus}
        hasIcon
        onClick={() => navigate('/ot/re100/fufl')}
      />
    </div>
  );
}